import React, { useEffect, useState, useCallback } from 'react'
import { Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom'
import { MainLayout } from '@/app/navigation'
import { bureauTabs, bureauNavigation } from './config/tabs.config'
import { ExpertiseDetailPage } from '@/ui/components/pages/expertise-detail'
import { PipelineTab } from '@/features/admin/tabs/pipeline'
import { TasksTab } from '@/features/admin/tabs/mes-taches'
import { SearchTab } from '@/features/admin/tabs/search'
import { PaymentTab } from '@/features/admin/tabs/payment'
import { ExpertTab } from '@/features/admin/tabs/expert'
import { IATab } from '@/features/admin/tabs/ia'
import { TrackerTab } from '@/features/admin/tabs/tracker'
import { TrackerDetailPage } from '@/features/admin/tabs/tracker/components/TrackerDetailPage'
import { EmailsTab } from '@/features/admin/tabs/emails'
import { SMSTab } from '@/features/admin/tabs/sms'
import { AppelsTab } from '@/features/admin/tabs/appels'
import { SettingsTab } from './tabs/settings/SettingsTab'
import { MonEspaceTab } from '@/features/admin/tabs/mon-espace/MonEspaceTab'
import { TicketTab } from '@/features/admin/tabs/ticket'
import { WhatsNewTab } from '@/features/admin/tabs/whats-new'
import { EquipeTab } from '@/features/admin/tabs/equipe'
import { MondayMeetingTab } from '@/features/admin/tabs/monday-meeting'
import { FacturationTab } from '@/features/admin/tabs/facturation'
import { BoardsTab } from '@/features/admin/tabs/boards'
import { useUnreadCount } from '@/features/admin/tabs/mes-taches/hooks/useUnreadCount'
import { UnreadProvider } from '@/features/admin/tabs/mes-taches/contexts/UnreadContext'
import { useUnpaidPaymentCount } from '@/features/admin/tabs/payment/hooks/useUnpaidPaymentCount'
import { useEmailTriageBadgeCount } from '@/features/admin/tabs/emails/hooks/useEmailTriageBadgeCount'
import { useVoicemailUnreadCount } from '@/features/admin/tabs/appels/hooks/useVoicemailUnreadCount'
import { useTrackerNotifications } from '@/features/admin/tabs/tracker/hooks/useTrackerNotifications'
import { useTrackerUnreadCount } from '@/features/admin/tabs/tracker/hooks/useTrackerUnreadCount'
import { TrackerUnreadProvider } from '@/features/admin/tabs/tracker/contexts/TrackerUnreadContext'
import { useTicketMentionCount } from '@/features/admin/tabs/ticket/hooks'
import { AgentLeaTab } from '@/features/admin/tabs/agent-lea'
import { useAgentPendingCount } from '@/features/admin/tabs/agent-lea/hooks/useAgentPendingCount'
import { useSMSUnreadCount } from '@/features/admin/tabs/sms/hooks/useSMSUnreadCount'
import { SMSUnreadProvider } from '@/features/admin/tabs/sms/contexts/SMSUnreadContext'
import { AffaireDetailPage } from '@/features/admin/tabs/affaire-detail'

// Wrapper component to extract reference from URL params
const ExpertiseDetailPageWrapper: React.FC = () => {
  const { reference } = useParams<{ reference: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we have expert context to return to their dossiers view
  const expertContext = location.state?.expertContext;
  const searchContext = location.state?.searchContext;
  const backUrl = location.state?.from || '/bureau/search';

  // Custom back handler that restores expert context if available
  const handleBack = () => {
    if (expertContext && backUrl === '/bureau/expert') {
      // Navigate back to expert tab with the selected expert
      navigate('/bureau/expert', {
        state: {
          selectedExpert: expertContext,
          viewMode: 'dossiers'
        }
      });
    } else if (searchContext && backUrl === '/bureau/search') {
      // Navigate back to search tab with the search context preserved
      const navState: any = {
        tab: searchContext.tab,
        searchName: searchContext.searchQuery
      };

      // If there's a selected customer, include it to show the affaire list
      if (searchContext.selectedCustomer) {
        navState.selectedCustomer = searchContext.selectedCustomer;
      }

      navigate('/bureau/search', { state: navState });
    } else {
      navigate(backUrl);
    }
  };

  return (
    <ExpertiseDetailPage
      dossierId={reference || ''}
      backUrl={backUrl}
      showActions={true}
      variant="full"
      onBack={handleBack}
    />
  );
}

// Inner component that has access to UnreadProvider context
const BureauLayoutContent: React.FC<{
  tabsWithBadge: any[]
  getBadgeValue: (tabId: string) => string | number | undefined
}> = ({ tabsWithBadge, getBadgeValue }) => {
  return (
    <MainLayout
      tabs={tabsWithBadge}
      navigationItems={bureauNavigation}
      getBadgeValue={getBadgeValue}
    >
        <Routes>
        {/* Expertise detail page (not in navigation) */}
        <Route 
          path="expertise/:reference" 
          element={
            <ExpertiseDetailPageWrapper />
          } 
        />
        
        {/* Tab routes */}
        <Route path="pipeline" element={<PipelineTab />} />
        <Route path="search" element={<SearchTab />} />
        <Route path="tasks" element={<TasksTab />} />
        <Route path="payment" element={<PaymentTab />} />
        <Route path="facturation" element={<FacturationTab />} />
        <Route path="expert" element={<ExpertTab />} />
        <Route path="ia" element={<IATab />} />
        <Route path="boards" element={<BoardsTab />} />
        <Route path="agent-lea" element={<AgentLeaTab />} />
        <Route path="affaire/:affaireId" element={<AffaireDetailPage />} />
        <Route path="tracker/:affaireId" element={<TrackerDetailPage />} />
        <Route path="tracker" element={<TrackerTab />} />
        <Route path="emails" element={<EmailsTab />} />
        <Route path="sms" element={<SMSTab />} />
        <Route path="appels" element={<AppelsTab />} />
        <Route path="ticket" element={<TicketTab />} />
        <Route path="monday-meeting" element={<MondayMeetingTab />} />
        <Route path="mon-espace" element={<MonEspaceTab />} />
        <Route path="equipe" element={<EquipeTab />} />
        <Route path="settings" element={<SettingsTab />} />
        <Route path="whats-new" element={<WhatsNewTab />} />
        {/* Default route - redirect to whats-new */}
        <Route path="/" element={<Navigate to="whats-new" replace />} />
        <Route index element={<Navigate to="whats-new" replace />} />
      </Routes>
    </MainLayout>
  );
}

// Inner component that uses tracker notifications (needs to be inside TrackerUnreadProvider)
const BureauLayoutWithTrackerNotifications: React.FC<{
  tabsWithBadge: any[]
  getBadgeValue: (tabId: string) => string | number | undefined
}> = ({ tabsWithBadge, getBadgeValue }) => {
  // Enable tracker notifications
  useTrackerNotifications({ enabled: true });

  return <BureauLayoutContent tabsWithBadge={tabsWithBadge} getBadgeValue={getBadgeValue} />;
};

export const BureauLayout: React.FC = () => {
  const { unreadCount, refetch } = useUnreadCount();
  const { unpaidCount } = useUnpaidPaymentCount();
  const { unreadCount: trackerUnreadCount, refetch: refetchTrackerUnread } = useTrackerUnreadCount();
  const { mentionCount: ticketMentionCount } = useTicketMentionCount();
  const { pendingCount: agentPendingCount } = useAgentPendingCount();
  const { unreadCount: smsUnreadCount, refetch: refetchSMSUnread } = useSMSUnreadCount();
  const { badgeCount: emailBadgeCount } = useEmailTriageBadgeCount();
  const { unreadCount: voicemailUnreadCount } = useVoicemailUnreadCount();
  const [tabsWithBadge, setTabsWithBadge] = useState(bureauTabs);

  // Callback to get badge value for a tab ID (used by grouped navigation)
  const getBadgeValue = useCallback(
    (tabId: string): string | number | undefined => {
      if (tabId === 'tasks' && unreadCount > 0) {
        return unreadCount;
      }
      if (tabId === 'payment' && unpaidCount > 0) {
        return unpaidCount;
      }
      if (tabId === 'tracker' && trackerUnreadCount > 0) {
        return trackerUnreadCount;
      }
      if (tabId === 'ticket' && ticketMentionCount > 0) {
        return ticketMentionCount;
      }
      if (tabId === 'agent-lea' && agentPendingCount > 0) {
        return agentPendingCount;
      }
      if (tabId === 'sms' && smsUnreadCount > 0) {
        return smsUnreadCount;
      }
      if (tabId === 'emails' && emailBadgeCount > 0) {
        return emailBadgeCount;
      }
      if (tabId === 'appels' && voicemailUnreadCount > 0) {
        return voicemailUnreadCount;
      }
      return undefined;
    },
    [unreadCount, unpaidCount, trackerUnreadCount, ticketMentionCount, agentPendingCount, smsUnreadCount, emailBadgeCount, voicemailUnreadCount]
  );

  useEffect(() => {
    // Update tabs with both tasks and tracker badges (for flat navigation fallback)
    const updatedTabs = bureauTabs.map((tab) => {
      if (tab.id === 'tasks' && unreadCount > 0) {
        return {
          ...tab,
          badge: {
            value: unreadCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // Payment badge
      if (tab.id === 'payment' && unpaidCount > 0) {
        return {
          ...tab,
          badge: {
            value: unpaidCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      if (tab.id === 'tracker' && trackerUnreadCount > 0) {
        return {
          ...tab,
          badge: {
            value: trackerUnreadCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // Ticket badge (for @mentions)
      if (tab.id === 'ticket' && ticketMentionCount > 0) {
        return {
          ...tab,
          badge: {
            value: ticketMentionCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // Agent Léa badge (pending plans)
      if (tab.id === 'agent-lea' && agentPendingCount > 0) {
        return {
          ...tab,
          badge: {
            value: agentPendingCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // SMS badge (unprocessed messages)
      if (tab.id === 'sms' && smsUnreadCount > 0) {
        return {
          ...tab,
          badge: {
            value: smsUnreadCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      // Emails badge (interesting + new_affaire)
      if (tab.id === 'emails' && emailBadgeCount > 0) {
        return {
          ...tab,
          badge: {
            value: emailBadgeCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      return tab;
    });
    setTabsWithBadge(updatedTabs);
  }, [unreadCount, unpaidCount, trackerUnreadCount, ticketMentionCount, agentPendingCount, smsUnreadCount, emailBadgeCount]);

  return (
    <UnreadProvider refetchUnreadCount={refetch}>
      <TrackerUnreadProvider refetchUnreadCount={refetchTrackerUnread}>
        <SMSUnreadProvider refetchUnreadCount={refetchSMSUnread}>
          <BureauLayoutWithTrackerNotifications
            tabsWithBadge={tabsWithBadge}
            getBadgeValue={getBadgeValue}
          />
        </SMSUnreadProvider>
      </TrackerUnreadProvider>
    </UnreadProvider>
  );
}